import React, { FC, useState } from 'react';
import {
  Avatar,
  Badge,
  Card,
  ColumnLayout,
  CopyButton,
  Empty,
  FlexGroup,
  FlexItem,
  KeyValueList,
  Link,
  Loading,
  Tag,
  Text,
  ViewLayout,
} from '@lithos';
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { useGetTransformServerGroup } from '../../../api/serverGroup';
import { Status, TransformServerGroup } from '../../../types/tecton_proto/servergroupservice/server_group_service';
import ServerGroupStatusBadge from './ServerGroupStatusBadge';
import TransformServerGroupFeatureViewsTableView from './TransformGroupsFeatureViewsTable';
import TransformServerGroupFeatureServicesTableView from './TransformGroupsFeatureServicesTable';
import { EnvironmentFlyout } from './TransformServerGroupsTableView';
import { CalloutProps } from '@lithos/Callout';
import { dateTimeFormat, timeZone } from '@lithos/DateUtils';
import TransformServerGroupMonitoring from './TransformServerGroupMonitoring';

const TransformGroupMetadataView: FC<{ serverGroup: TransformServerGroup }> = ({ serverGroup }) => {
  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);

  const metadataItems = [
    {
      title: 'Name',
      content: serverGroup.name,
    },
    {
      title: 'ID',
      content: (
        <FlexGroup padding="0" gapSize="2" direction="row" alignItems="center">
          <FlexItem grow={0}>
            <Text code>{serverGroup.id}</Text>
          </FlexItem>
          <FlexItem grow={0} shrink={0}>
            <CopyButton code={serverGroup.id ?? ''} />
          </FlexItem>
        </FlexGroup>
      ),
    },
    {
      title: 'Status',
      content: <ServerGroupStatusBadge status={serverGroup.status ?? Status.UNSPECIFIED} />,
    },
    {
      title: 'Type',
      content: 'Transform Server Group',
    },
    {
      title: 'Owner',
      content: (
        <FlexGroup padding="0" gapSize="2">
          {serverGroup.metadata?.owner ? (
            <FlexItem grow={0}>
              <Avatar name={serverGroup.metadata?.owner} />
              <Text>{serverGroup.metadata?.owner}</Text>
            </FlexItem>
          ) : (
            <Text>-</Text>
          )}
        </FlexGroup>
      ),
    },

    {
      title: 'Created At',
      content: serverGroup.created_at ? dateTimeFormat(new Date(serverGroup.created_at), timeZone.UTC) : '-',
    },
    {
      title: 'Last Updated At',
      content: serverGroup.updated_at ? dateTimeFormat(new Date(serverGroup.updated_at), timeZone.UTC) : '-',
    },
    {
      title: 'Tags',
      content: serverGroup.metadata?.tags
        ? Object.entries(serverGroup.metadata.tags).map(([key, value]) => {
            return <Tag category={key} label={value} size="s" icon />;
          })
        : [],
    },
  ];

  const configItems = [
    {
      title: 'Node Type',
      content: (
        <FlexGroup padding="0" gapSize="2" direction="row">
          <FlexItem grow="0" shrink="0">
            {serverGroup.node_type}
          </FlexItem>
          <FlexItem grow="0" shrink="0">
            {serverGroup.pending_config?.node_type && (
              <Badge label={`Pending: ${serverGroup.pending_config?.node_type}`} color="gray" size="s" icon="Refresh" />
            )}
          </FlexItem>
        </FlexGroup>
      ),
    },
    {
      title: 'Scaling Mode',
      content: (
        <FlexGroup padding="0" gapSize="2" direction="row">
          <FlexItem grow="0" shrink="0">
            {serverGroup.autoscaling_config ? 'Autoscaling' : 'Provisioned'}
          </FlexItem>
          <FlexItem grow="0" shrink="0">
            {serverGroup.pending_config?.autoscaling_config && (
              <Badge label={`Pending: Autoscaling}`} color="gray" size="s" icon="Refresh" />
            )}
            {serverGroup.pending_config?.provisioned_config && (
              <Badge label={`Pending: Provisioned}`} color="gray" size="s" icon="Refresh" />
            )}
          </FlexItem>
        </FlexGroup>
      ),
    },
    ...(serverGroup.provisioned_config
      ? [
          {
            title: 'Desired Nodes',
            content: (
              <FlexGroup padding="0" gapSize="2" direction="row">
                <FlexItem grow="0" shrink="0">
                  {serverGroup.provisioned_config?.desired_nodes}
                </FlexItem>
                <FlexItem grow="0" shrink="0">
                  {serverGroup.pending_config?.provisioned_config?.desired_nodes && (
                    <Badge label={serverGroup.provisioned_config?.desired_nodes} color="gray" size="s" icon="Refresh" />
                  )}
                </FlexItem>
              </FlexGroup>
            ),
          },
        ]
      : []),
    ...(serverGroup.autoscaling_config
      ? [
          {
            title: 'Min Nodes',
            content: (
              <FlexGroup padding="0" gapSize="2" direction="row">
                <FlexItem grow="0" shrink="0">
                  {serverGroup.autoscaling_config?.min_nodes ?? '-'}
                </FlexItem>
                <FlexItem grow="0" shrink="0">
                  {serverGroup.pending_config?.autoscaling_config?.min_nodes && (
                    <Badge label={serverGroup.autoscaling_config?.min_nodes} color="gray" size="s" icon="Refresh" />
                  )}
                </FlexItem>
              </FlexGroup>
            ),
          },
          {
            title: 'Max Nodes',
            content: (
              <FlexGroup padding="0" gapSize="2" direction="row">
                <FlexItem grow="0" shrink="0">
                  {serverGroup.autoscaling_config?.max_nodes ?? '-'}
                </FlexItem>
                <FlexItem grow="0" shrink="0">
                  {serverGroup.pending_config?.autoscaling_config?.min_nodes && (
                    <Badge label={serverGroup.autoscaling_config?.min_nodes} color="gray" size="s" icon="Refresh" />
                  )}
                </FlexItem>
              </FlexGroup>
            ),
          },
        ]
      : []),

    {
      title: 'Environment',
      content: (
        <FlexGroup padding="0" gapSize="2" direction="row">
          <FlexItem grow="0" shrink="0">
            {serverGroup.environment ? (
              <Link icon="Compute" onClick={() => setIsFlyoutOpen(true)} name={serverGroup.environment} />
            ) : (
              <Text>-</Text>
            )}
          </FlexItem>
          <FlexItem grow="0" shrink="0">
            {serverGroup.pending_config?.environment && (
              <Badge label={serverGroup.pending_config?.environment} color="gray" size="s" icon="Refresh" />
            )}
          </FlexItem>
        </FlexGroup>
      ),
    },
    {
      title: 'Environment Variables',
      content: (
        <FlexGroup direction="column" padding="0" gapSize="0.5">
          <FlexItem>
            <FlexGroup direction="row" padding="0" gapSize="0.5">
              {Object.entries(serverGroup.environment_variables || {}).map((envVar: [string, string]) => {
                return (
                  <FlexItem grow="0" shrink="0">
                    <Tag category={envVar[0]} label={envVar[1] as string} size="s" icon />
                  </FlexItem>
                );
              })}
            </FlexGroup>
          </FlexItem>
          <FlexItem>
            <FlexGroup direction="row" padding="0" gapSize="0.5">
              {Object.entries(serverGroup.pending_config?.environment_variables || {}).map(
                (envVar: [string, string]) => {
                  return (
                    <FlexItem grow="0" shrink="0">
                      <Badge label={`Pending:${envVar[0]} ${envVar[1] as string}`} size="xs" icon="Refresh" />
                    </FlexItem>
                  );
                }
              )}
            </FlexGroup>
          </FlexItem>
        </FlexGroup>
      ),
    },
  ];

  return (
    <>
      <ColumnLayout>
        <Card title="Metadata">
          <KeyValueList items={metadataItems} />
        </Card>
        <Card title="Configuration">
          <KeyValueList items={configItems} />
        </Card>
      </ColumnLayout>
      {isFlyoutOpen && serverGroup.environment && (
        <EnvironmentFlyout environment={serverGroup.environment} closeCallback={() => setIsFlyoutOpen(false)} />
      )}
    </>
  );
};

const TransformServerGroupContainer = () => {
  const { workspace } = useParams();
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useGetTransformServerGroup(workspace ?? '', id ?? '');
  const pathSegments = location.pathname.split('/');
  const currentPath = pathSegments[pathSegments.length - 1];
  const selectedTab =
    currentPath === 'metadata'
      ? 'Metadata'
      : currentPath === 'feature-views'
      ? 'Feature Views'
      : currentPath === 'feature-services'
      ? 'Feature Services'
      : 'Monitoring';

  if (isLoading) {
    return (
      <ViewLayout
        content={<Loading />}
        header={{ title: '', breadcrumbs: { root: { label: workspace ?? '', url: '' }, items: [] } }}
      />
    );
  }

  if (isError) {
    return (
      <ViewLayout
        header={{
          title: '',
          breadcrumbs: { root: { label: '', url: '' }, items: [] },
        }}
        content={
          <Empty
            placement="full-page"
            actions={[]}
            useCase="errorUnknown"
            title="Unknown Error"
            description="Error loading transform server group"
          />
        }
      />
    );
  }

  const transformServerGroupRoutes = [
    {
      label: 'Metadata',
      route: 'metadata',
    },
    {
      label: 'Monitoring',
      route: 'monitoring',
    },
    {
      label: 'Feature Views',
      route: 'feature-views',
    },
    {
      label: 'Feature Services',
      route: 'feature-services',
    },
  ];

  const calloutProps: Record<Status, CalloutProps | undefined> = {
    [Status.UNSPECIFIED]: undefined,
    [Status.ERROR]: undefined,
    [Status.PENDING]: {
      title: 'Updating Configuration',
      content: 'Changes typically take effect within 1 minute.',
      singleLine: true,
    },
    [Status.READY]: undefined,
    [Status.CREATING]: {
      title: 'Transform Server Group',
      content: 'A Transform Server Group is a collection of feature views that are used to transform data.',
      singleLine: true,
    },
    [Status.UPDATING]: {
      title: 'Updating Configuration',
      content: 'Changes typically take effect within 1 minute.',
      singleLine: true,
    },
    [Status.DELETING]: {
      title: 'Deletion in Progress',
      content:
        'This Transform Server Group is scheduled for deletion and will be removed from the database within minutes.',
      singleLine: true,
      variant: 'warning',
    },
  };

  return (
    <ViewLayout
      header={{
        breadcrumbs: {
          root: {
            label: workspace ?? '',
            url: `/repo/${workspace}`,
          },
          items: [
            {
              label: 'Server Groups',
              url: `/repo/${workspace}/server-groups`,
            },
            {
              label: 'Transform Server Groups',
              url: `/repo/${workspace}/server-groups/transform-server-groups`,
            },
            {
              label: data.name,
            },
          ],
        },
        callout: calloutProps[data.status ?? Status.UNSPECIFIED],
        tabs: {
          items: transformServerGroupRoutes,
          selectedTab: selectedTab,
          setSelectedTab: (tab: string) => {
            const route = transformServerGroupRoutes.find((route) => route.label === tab)?.route;

            if (route) {
              navigate(route);
            }
          },
        },
        title: data.name ?? '',
      }}
      content={
        <Routes>
          <Route index element={<Navigate to={'metadata'} />} />
          <Route path={'metadata'} element={<TransformGroupMetadataView serverGroup={data} />} />
          <Route path={'monitoring'} element={<TransformServerGroupMonitoring />} />
          <Route path={'feature-views'} element={<TransformServerGroupFeatureViewsTableView serverGroup={data} />} />
          <Route
            path={'feature-services'}
            element={<TransformServerGroupFeatureServicesTableView serverGroup={data} />}
          />
        </Routes>
      }
    />
  );
};

export default TransformServerGroupContainer;
